import { unique } from '@portals-platform/utils';
import type { Page } from '../api/common.js';
import type {
  CaptivePolicyResponse,
  CountryAssuredsResponse,
  CountryResponse,
  LocationLimitResponse,
} from '../api/responses.js';
import { formatDateForInput } from '../dates.js';
import type { CaptivePolicy, CountryOption } from '../models.js';

export const toCaptivePolicies = (page: Page<CaptivePolicyResponse> | undefined): CaptivePolicy[] =>
  (page?.content ?? []).map((policy) => ({
    id: policy.id,
    policyYear: policy.policyYear,
    totalLimitCurrency: policy.totalLimit?.currency || 'USD',
    startDate: formatDateForInput(policy.startDate),
    endDate: formatDateForInput(policy.endDate),
  }));

export const toCountryOptions = (countries: CountryResponse[] | undefined): CountryOption[] => {
  const options = (countries ?? [])
    .filter((country) => country.countryCode && country.countryName)
    .map((country) => ({
      value: country.countryCode,
      label: country.countryName,
    }));

  return Array.from(new Map(options.map((option) => [option.value, option])).values()).sort(
    (a, b) => a.label.localeCompare(b.label),
  );
};

export const toAssuredsByCountry = (
  assureds: CountryAssuredsResponse | undefined,
): Record<string, string[]> =>
  Object.fromEntries(
    Object.entries(assureds ?? {}).map(([countryCode, rows]) => [
      countryCode,
      unique(rows.map((row) => row.assured)),
    ]),
  );

export const toStorageLocationOptions = (
  ...limits: (LocationLimitResponse[] | undefined)[]
): string[] =>
  unique(limits.flatMap((list) => list ?? []).map((limit) => limit.locationName)).sort();
